// the native SHEET 27 order sheet for one season — an editable, in-app table (the website is the
// source of truth, not the Excel). One row per vessel that has been PUT ON the sheet (uploaded from a
// SHEET 27 workbook or hand-entered) — it is not auto-filled from the whole season overview, which used
// to mix in boats nobody had added and caused confusion. The ~111 spec columns are click-to-edit cells
// stored per vessel, plus the printed-for-Assembly date, leader-check and comments. A search box, a
// model filter and Excel-style column filters (blanks, "+" or any value, on every column) narrow the
// rows, "Import from Excel" seeds the sheet from an uploaded SHEET 27 workbook
// (matched by SAP), and a row can be removed (hidden) from the sheet. Departed boats (marked "Left" on
// the Overview) are kept out so last season's vessels don't linger.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ORDER_SHEET_COLUMNS } from '@technohull/shared';
import {
  useAssemblyOrders,
  useDeleteOrderSheetRow,
  useImportOrderSheet,
  useOrderSheet,
  useOverview,
  useRestoreOrderSheetRow,
  useSetAssemblyOrder,
  useSetOrderSheetCell,
} from '../features/hooks.js';
import { orderSheetRoster, parseOrderSheetWorkbook, resolveImportRows } from '../features/orderSheetImport.js';
import { BLANK, columnValues, filterValue, passesFilters, setColumnFilter, type ColumnFilters } from '../features/orderSheetFilter.js';
import { EmptyState, ErrorState, InlineError, LoadingState, Modal, useConfirm } from './ui.js';
import { ColumnFilter } from './ColumnFilter.js';
import { IconFilter, IconUpload } from './icons.js';
import { fold, hasText } from '../lib/search.js';
import { useDateFormatter } from '../lib/format.js';

// every column that can be filtered: the three status columns, then each spec column
const FILTER_COLUMNS: ReadonlyArray<{ id: string; label: string }> = [
  { id: '@printed', label: 'Printed' },
  { id: '@leader', label: 'Leader ✓' },
  { id: '@comments', label: 'Comments' },
  ...ORDER_SHEET_COLUMNS.map((c) => ({ id: c.key, label: c.label.replace(/:$/, '') })),
];
const FILTER_LABEL = new Map(FILTER_COLUMNS.map((c) => [c.id, c.label]));
// the column filters last for the tab, so a look at Procurement and back keeps them
const FILTERS_KEY = 'rolodex.orderSheetFilters';
function loadFilters(): ColumnFilters {
  try {
    const raw = JSON.parse(sessionStorage.getItem(FILTERS_KEY) ?? '{}') as unknown;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    return Object.fromEntries(Object.entries(raw).filter(([k, v]) => FILTER_LABEL.has(k) && Array.isArray(v) && v.every((x) => typeof x === 'string'))) as ColumnFilters;
  } catch {
    return {};
  }
}

export function OrderSheetGrid({ season, canWrite }: { season: string; canWrite: boolean }) {
  const df = useDateFormatter();
  const overview = useOverview(season || undefined);
  const sheetQ = useOrderSheet();
  const ordersQ = useAssemblyOrders();
  const setCell = useSetOrderSheetCell();
  const setStatus = useSetAssemblyOrder();
  const importSheet = useImportOrderSheet();
  const delRow = useDeleteOrderSheetRow();
  const restoreRow = useRestoreOrderSheetRow();
  const fileInput = useRef<HTMLInputElement>(null);

  const [q, setQ] = useState('');
  const [model, setModel] = useState('');
  const [editing, setEditing] = useState<{ key: string; col: string } | null>(null);
  const [msg, setMsg] = useState('');
  const [confirmDel, setConfirmDel] = useState<{ key: string; name: string; sap: string } | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const [importErr, setImportErr] = useState<unknown>(null);
  const [confirmNode, confirmDialog] = useConfirm();
  const [filters, setFilters] = useState<ColumnFilters>(loadFilters);
  const [openFilter, setOpenFilter] = useState<{ col: string; anchor: HTMLElement } | null>(null);
  useEffect(() => {
    try { sessionStorage.setItem(FILTERS_KEY, JSON.stringify(filters)); } catch { /* private window: they just don't last */ }
  }, [filters]);

  const sheet = sheetQ.data?.sheet ?? {};
  const statuses = ordersQ.data?.orders ?? {};
  const hidden = useMemo(() => new Set(sheetQ.data?.hidden ?? []), [sheetQ.data]);

  // every overview vessel, keyed by SAP — used to name the removed vessels in the restore dialog.
  const vesselBySap = useMemo(
    () => new Map((overview.data?.vessels ?? []).map((v) => [v.sap, v] as const)),
    [overview.data],
  );
  // the order-sheet roster: the season's overview vessels PLUS any imported-only boats (with or without
  // a SAP), minus departed ("Left") boats and anything removed by hand. Keyed by a stable storage key.
  const allVessels = useMemo(
    () => orderSheetRoster(overview.data?.vessels ?? [], sheet, hidden),
    [overview.data, sheet, hidden],
  );
  const models = useMemo(
    () => Array.from(new Set(allVessels.map((v) => v.model).filter(Boolean))).sort(),
    [allVessels],
  );
  // the search and model filter first; the column filters then narrow these
  const searched = useMemo(() => {
    const needle = fold(q.trim());
    const rows = allVessels.filter((v) => {
      if (model && v.model !== model) return false;
      if (!needle) return true;
      if (hasText(needle, v.name, v.sap, v.model)) return true;
      const data = sheet[v.key];
      return !!data && Object.values(data).some((val) => hasText(needle, val));
    });
    return [...rows].sort((a, b) => a.name.localeCompare(b.name));
  }, [allVessels, model, q, sheet]);
  // what a vessel's cell in a column is filtered by ('' = blank)
  const filterValueOf = useCallback((v: { key: string }, col: string): string => {
    const s = statuses[v.key];
    if (col === '@printed') return filterValue(s?.printedAt);
    if (col === '@leader') return s?.leaderChecked ? 'yes' : BLANK;
    if (col === '@comments') return filterValue(s?.comments);
    return filterValue(sheet[v.key]?.[col]);
  }, [sheet, statuses]);
  const vessels = useMemo(
    () => searched.filter((v) => passesFilters(v, filters, filterValueOf)),
    [searched, filters, filterValueOf],
  );
  const filterCount = Object.keys(filters).length;
  // the open column's values, over the vessels its own filter isn't hiding
  const openValues = useMemo(() => {
    if (!openFilter) return [];
    const col = openFilter.col;
    return columnValues(searched.filter((v) => passesFilters(v, filters, filterValueOf, col)), (v) => filterValueOf(v, col), filters[col]);
  }, [openFilter, searched, filters, filterValueOf]);
  const closeFilter = useCallback(() => setOpenFilter(null), []);
  const toggleFilter = (col: string, anchor: HTMLElement) =>
    setOpenFilter((o) => (o?.col === col && o.anchor === anchor ? null : { col, anchor }));

  const removedList = useMemo(
    () => [...hidden].map((key) => ({
      key,
      name: vesselBySap.get(key)?.name ?? (key.startsWith('name:') ? key.slice(5) : ''),
      model: vesselBySap.get(key)?.model ?? '',
      sap: key.startsWith('name:') ? '' : key,
    })),
    [hidden, vesselBySap],
  );

  const doImport = async (file: File) => {
    setImportErr(null);
    setMsg('Reading workbook…');
    try {
      const XLSX: unknown = await import('xlsx');
      const parsed = parseOrderSheetWorkbook(XLSX, await file.arrayBuffer());
      // match against the season's overview vessels (by SAP, then by name); the rest are kept anyway.
      const { rows, orphans, skipped } = resolveImportRows(parsed, overview.data?.vessels ?? []);
      if (rows.length === 0) { setMsg('No vessels found in this file.'); return; }
      const extra =
        (orphans ? ` ${orphans} not on the season overview will be added.` : '') +
        (skipped ? ` ${skipped} unreadable line(s) skipped.` : '');
      const ok = await confirmDialog({
        title: 'Import order data',
        message: <>Import order data for {rows.length} vessel(s)?{extra}<br /><br />This replaces the stored cells for those vessels.</>,
        confirmLabel: 'Import',
      });
      if (!ok) { setMsg(''); return; }
      const res = await importSheet.mutateAsync({ rows });
      setMsg(`Imported ${res.count} vessel(s)${orphans ? ` (${orphans} new to this season)` : ''}.`);
    } catch (e) {
      // clear the 'Reading workbook…' progress line; the reason shows as a dismissable error instead
      setMsg('');
      setImportErr(e instanceof Error ? e : new Error('Could not import this file.'));
    }
  };

  if (overview.isLoading || sheetQ.isLoading) return <LoadingState label="Loading order sheet…" />;
  // only a failed first load replaces the grid; a failed background refetch keeps the cached rows on screen
  const loadErr = (!overview.data && overview.error) || (!sheetQ.data && sheetQ.error);
  if (loadErr) return <ErrorState error={loadErr} onRetry={() => { void overview.refetch(); void sheetQ.refetch(); }} />;
  // the cell / status / remove / restore hooks roll back optimistically on failure, so say why it snapped back
  const mutErr = setCell.error ?? setStatus.error ?? delRow.error ?? restoreRow.error;

  const cellVal = (rowKey: string, col: string) => sheet[rowKey]?.[col] ?? '';
  // how a filter value reads, in the filter list and the toolbar chips
  const valueName = (col: string, v: string) => {
    if (col === '@leader') return v === BLANK ? '(Not checked)' : 'Checked';
    if (v === BLANK) return '(Blanks)';
    return col === '@printed' ? df.date(v) || v : v;
  };
  const filterSummary = (col: string) => {
    const keep = filters[col] ?? [];
    if (!keep.length) return 'nothing';
    const names = keep.map((v) => valueName(col, v));
    return names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.join(', ');
  };
  const header = (id: string, label: string) => {
    const on = id in filters;
    return (
      <span className="os-th">
        <span>{label}</span>
        <button
          type="button"
          className={`os-fbtn${on ? ' on' : ''}`}
          aria-label={on ? `${label}: filtered to ${filterSummary(id)}. Change the filter` : `Filter ${label}`}
          aria-haspopup="dialog"
          aria-expanded={openFilter?.col === id}
          title={on ? `Showing ${filterSummary(id)}` : `Filter ${label}`}
          onClick={(e) => toggleFilter(id, e.currentTarget)}
        >
          <IconFilter size={11} filled={on} />
        </button>
      </span>
    );
  };
  const commit = (rowKey: string, col: string, value: string) => {
    if (value !== cellVal(rowKey, col)) setCell.mutate({ sap: rowKey, cells: { [col]: value } });
    setEditing(null);
  };

  return (
    <div style={{ marginBottom: 18 }}>
      {/* phones: the count on its own line, search + model share the next, Removed / Import below */}
      <div className="os-toolbar">
        <span className="field-label os-count">Order sheet - {vessels.length}/{allVessels.length} vessel(s)</span>
        <span className="os-spacer" />
        {(setCell.isPending || setStatus.isPending || importSheet.isPending || delRow.isPending) && <span className="muted os-saving">Saving…</span>}
        <input className="field os-search" placeholder="Search…" aria-label="Search the order sheet" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field os-model" aria-label="Model" value={model} onChange={(e) => setModel(e.target.value)}>
          <option value="">All models</option>
          {models.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        {/* any column's filter from here too (the funnel in each column's header opens the same one) */}
        <select
          className="field os-colfilter"
          aria-label="Filter a column"
          value=""
          onChange={(e) => { const col = e.target.value; if (col) setOpenFilter({ col, anchor: e.currentTarget }); }}
        >
          <option value="">Filter a column…</option>
          {FILTER_COLUMNS.map((c) => <option key={c.id} value={c.id}>{c.id in filters ? `● ${c.label}` : c.label}</option>)}
        </select>
        {hidden.size > 0 && (
          <button className="btn btn-sm btn-ghost" onClick={() => setShowRemoved(true)} title="Vessels removed from the order sheet">
            Removed ({hidden.size})
          </button>
        )}
        {canWrite && (
          <>
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void doImport(f); e.target.value = ''; }}
            />
            <button className="btn btn-sm" disabled={importSheet.isPending} onClick={() => fileInput.current?.click()}>
              <IconUpload size={14} /> Import from Excel
            </button>
          </>
        )}
      </div>
      {filterCount > 0 && (
        <div className="os-filters" aria-label="Column filters">
          {Object.keys(filters).map((col) => (
            <span key={col} className="os-fchip">
              <button type="button" className="os-fchip-open" title="Change this filter" onClick={(e) => toggleFilter(col, e.currentTarget)}>
                <b>{FILTER_LABEL.get(col) ?? col}</b> {filterSummary(col)}
              </button>
              <button
                type="button"
                className="os-fchip-x"
                aria-label={`Stop filtering ${FILTER_LABEL.get(col) ?? col}`}
                onClick={() => { setFilters((f) => setColumnFilter(f, col, null, [])); if (openFilter?.col === col) setOpenFilter(null); }}
              >✕</button>
            </span>
          ))}
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setFilters({}); setOpenFilter(null); }}>
            Clear filters ({filterCount})
          </button>
        </div>
      )}
      {openFilter && (
        <ColumnFilter
          label={FILTER_LABEL.get(openFilter.col) ?? openFilter.col}
          anchor={openFilter.anchor}
          values={openValues}
          keep={filters[openFilter.col] ?? null}
          onChange={(keep) => setFilters((f) => setColumnFilter(f, openFilter.col, keep, openValues))}
          onClose={closeFilter}
          name={(v) => valueName(openFilter.col, v)}
        />
      )}
      {msg && <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>{msg}</div>}
      {importErr != null && <InlineError error={importErr} onDismiss={() => setImportErr(null)} />}
      {mutErr && (
        <InlineError error={mutErr} onDismiss={() => { setCell.reset(); setStatus.reset(); delRow.reset(); restoreRow.reset(); }} />
      )}
      {ordersQ.error && !ordersQ.data && <InlineError error={ordersQ.error} />}

      {allVessels.length === 0 ? (
        <EmptyState
          title="No vessels on the order sheet"
          hint={canWrite
            ? 'Import a SHEET 27 workbook to add vessels - the order sheet lists only uploaded boats, not the whole overview.'
            : 'No vessels have been uploaded to the order sheet yet.'}
        />
      ) : (
        <div className="osheet-wrap">
          <table className="osheet">
            <thead>
              <tr>
                <th className="stick">Vessel</th>
                <th title="Date the order was printed for Assembly" className={'@printed' in filters ? 'os-filtered' : undefined}>{header('@printed', 'Printed')}</th>
                <th title="Team / model leader checked the order" className={'@leader' in filters ? 'os-filtered' : undefined}>{header('@leader', 'Leader ✓')}</th>
                <th className={'@comments' in filters ? 'os-filtered' : undefined}>{header('@comments', 'Comments')}</th>
                {ORDER_SHEET_COLUMNS.map((c) => (
                  <th key={c.key} title={c.label} className={c.key in filters ? 'os-filtered' : undefined}>{header(c.key, c.label.replace(/:$/, ''))}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vessels.map((v) => {
                const s = statuses[v.key];
                const who = v.name || v.sap || '(unnamed)';
                return (
                  // data-sap: a co-op pointer, field or scroll position lands on this boat's row for everyone
                  <tr key={v.key} data-sap={v.sap || undefined}>
                    <td className="stick">
                      <div className="os-vessel">
                        <div>
                          <div className="nm">{v.name || v.sap || '(unnamed)'}</div>
                          <div className="sub">{v.sap || '- no SAP'}{v.model ? ` · ${v.model}` : ''}</div>
                        </div>
                        {canWrite && (
                          <button type="button" className="os-del" title="Remove this vessel from the order sheet" aria-label={`Remove ${who} from the order sheet`} onClick={() => setConfirmDel({ key: v.key, name: v.name || v.sap, sap: v.sap })}>✕</button>
                        )}
                      </div>
                    </td>
                    <td>
                      {/* editors pick the date; everyone else reads it in their own date format */}
                      {canWrite ? (
                        <input type="date" className="field os-date" aria-label={`Printed date, ${who}`}
                          value={s?.printedAt ?? ''} onChange={(e) => setStatus.mutate({ sap: v.key, printedAt: e.target.value })} />
                      ) : (
                        <span>{df.date(s?.printedAt) || '-'}</span>
                      )}
                    </td>
                    <td className="os-chk-td" style={{ textAlign: 'center' }}>
                      {/* the label fills the cell on touch, so the whole cell ticks the box */}
                      <label className="os-chk">
                        <input type="checkbox" disabled={!canWrite} checked={!!s?.leaderChecked} aria-label={`Leader checked, ${who}`}
                          onChange={(e) => setStatus.mutate({ sap: v.key, leaderChecked: e.target.checked })} />
                      </label>
                    </td>
                    <td>
                      <input className="field os-cmt" disabled={!canWrite} placeholder="-" aria-label={`Comments, ${who}`}
                        defaultValue={s?.comments ?? ''} key={`c:${v.key}:${s?.comments ?? ''}`}
                        onBlur={(e) => { if (e.target.value !== (s?.comments ?? '')) setStatus.mutate({ sap: v.key, comments: e.target.value }); }} />
                    </td>
                    {ORDER_SHEET_COLUMNS.map((c) => {
                      const val = cellVal(v.key, c.key);
                      const isEditing = editing?.key === v.key && editing?.col === c.key;
                      const colName = c.label.replace(/:$/, '');
                      return (
                        <td key={c.key} onClick={() => { if (canWrite && !isEditing) setEditing({ key: v.key, col: c.key }); }}>
                          {isEditing ? (
                            <input
                              className="field os-edit"
                              autoFocus
                              aria-label={`${colName}, ${who}`}
                              defaultValue={val}
                              onBlur={(e) => commit(v.key, c.key, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                else if (e.key === 'Escape') setEditing(null);
                              }}
                            />
                          ) : canWrite ? (
                            // a real button so Enter / Space reach the td's click-to-edit (the click bubbles up)
                            <button type="button" className={`cellbtn ${val ? '' : 'empty'}`} aria-label={`${colName}, ${who}${val ? `: ${val}` : ''}`}>{val}</button>
                          ) : (
                            <span className={`cellbtn ${val ? '' : 'empty'}`}>{val}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {vessels.length === 0 && (
                <tr><td colSpan={4 + ORDER_SHEET_COLUMNS.length}><EmptyState title="No vessels match" hint={filterCount ? 'Try a different search, model or column filter, or clear the column filters.' : 'Try a different search or model filter.'} /></td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {confirmDel && (
        <Modal
          title="Remove from order sheet?"
          onClose={() => setConfirmDel(null)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => { delRow.mutate({ sap: confirmDel.key }); setConfirmDel(null); }}>Remove</button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            Remove <b>{confirmDel.name}</b> <span className="muted">({confirmDel.sap || 'no SAP'})</span> from the order sheet and its procurement totals?
          </p>
          <p className="muted" style={{ fontSize: 12 }}>
            The boat stays on the Smartsheet Overview - this only takes it off the order sheet, and any spec
            data entered here for it is cleared. You can restore it later from “Removed”.
          </p>
        </Modal>
      )}

      {showRemoved && (
        <Modal title={`Removed vessels (${removedList.length})`} onClose={() => setShowRemoved(false)} width={460}
          footer={<button className="btn btn-ghost" onClick={() => setShowRemoved(false)}>Close</button>}>
          {removedList.length === 0 && <p className="muted" style={{ marginTop: 0 }}>Nothing removed.</p>}
          {removedList.map((r) => (
            <div key={r.key} className="ov-row">
              <span>{r.name || <span className="muted">(not on this season)</span>} <span className="muted" style={{ fontSize: 11 }}>· {r.sap || 'no SAP'}{r.model ? ` · ${r.model}` : ''}</span></span>
              {canWrite && (
                <button className="btn btn-sm btn-ghost" disabled={restoreRow.isPending} onClick={() => restoreRow.mutate({ sap: r.key })}>Restore</button>
              )}
            </div>
          ))}
        </Modal>
      )}
      {confirmNode}
    </div>
  );
}
